// Pure helpers behind TreeSelect. The tree is flattened once into depth-first
// order, so each node's descendants (and its leaves) sit in one contiguous
// slice. Selection counts, search and row building then become plain loops
// over arrays instead of tree walks, which is what keeps the picker
// responsive at 10,000+ leaves.

// A node is a leaf when it has no `children` array and no `status`. A branch
// with `children: []` is still a branch; it just has no leaves.
export function normalizeTree(nodes) {
    const flat = []
    const leaves = []
    const leafFlatIndex = []
    let rootOrdinal = -1

    const walk = (list, depth, parentIndex) => {
        for (const node of list) {
            const index = flat.length
            const label = node.label === null || node.label === undefined ? '' : String(node.label)
            const status = node.status ?? null
            const isLeaf = ! Array.isArray(node.children) && status === null
            if (depth === 0) {
                rootOrdinal++
            }

            const entry = {
                index,
                key: node.key,
                label,
                labelLower: label.toLowerCase(),
                depth,
                parentIndex,
                rootOrdinal,
                isLeaf,
                status,
                leafStart: leaves.length,
                leafEnd: leaves.length,
                end: index + 1,
                node,
            }
            flat.push(entry)

            if (isLeaf) {
                leaves.push(node.key)
                leafFlatIndex.push(index)
            } else if (Array.isArray(node.children)) {
                walk(node.children, depth + 1, index)
            }

            entry.leafEnd = leaves.length
            entry.end = flat.length
        }
    }

    walk(nodes ?? [], 0, -1)

    return {flat, leaves, leafFlatIndex}
}

// null when there is no query. Otherwise:
// - visible[i]: the node matches, an ancestor matches, or a descendant matches
// - reveal[i]: a descendant matches, so the node is forced open to show it
// A plain substring match, so "(" or "." in the query is literal.
export function searchMask(flat, query) {
    const needle = (query ?? '').trim().toLowerCase()
    if (needle === '') {
        return null
    }

    const count = flat.length
    const self = new Uint8Array(count)
    const fromAncestor = new Uint8Array(count)
    const reveal = new Uint8Array(count)

    // Parents come before children, so one forward pass carries ancestor matches down.
    for (let i = 0; i < count; i++) {
        self[i] = flat[i].labelLower.includes(needle) ? 1 : 0
        const parent = flat[i].parentIndex
        if (parent !== -1 && (self[parent] === 1 || fromAncestor[parent] === 1)) {
            fromAncestor[i] = 1
        }
    }

    // And one backward pass carries descendant matches up.
    for (let i = count - 1; i >= 0; i--) {
        const parent = flat[i].parentIndex
        if (parent !== -1 && (self[i] === 1 || reveal[i] === 1)) {
            reveal[parent] = 1
        }
    }

    const visible = new Uint8Array(count)
    for (let i = 0; i < count; i++) {
        visible[i] = self[i] | fromAncestor[i] | reveal[i]
    }

    return {visible, reveal}
}

// The rows to render, in order. A hidden or collapsed node's whole subtree is
// skipped by jumping to its `end`.
export function buildRows(flat, mask, isExpanded) {
    const rows = []
    let i = 0
    while (i < flat.length) {
        const entry = flat[i]
        if (mask !== null && mask.visible[i] === 0) {
            i = entry.end
            continue
        }

        rows.push(entry)
        if (entry.isLeaf) {
            i++
            continue
        }

        const open = (mask !== null && mask.reveal[i] === 1) || isExpanded(entry)
        i = open ? i + 1 : entry.end
    }

    return rows
}

// Prefix sums over the leaves. total[j] counts leaves[0..j) that are visible
// under the search; selected[j] counts those that are also selected. Any
// node's counts are then two subtractions over its leaf slice.
export function leafPrefixes(leaves, leafFlatIndex, selectedSet, mask) {
    const total = new Int32Array(leaves.length + 1)
    const selected = new Int32Array(leaves.length + 1)
    for (let j = 0; j < leaves.length; j++) {
        const counted = mask === null || mask.visible[leafFlatIndex[j]] === 1
        total[j + 1] = total[j] + (counted ? 1 : 0)
        selected[j + 1] = selected[j] + (counted && selectedSet.has(leaves[j]) ? 1 : 0)
    }

    return {total, selected}
}

export function nodeLeafCount(entry, prefixes) {
    return prefixes.total[entry.leafEnd] - prefixes.total[entry.leafStart]
}

export function nodeState(entry, prefixes) {
    const total = nodeLeafCount(entry, prefixes)
    if (total === 0) {
        return 'none'
    }

    const count = prefixes.selected[entry.leafEnd] - prefixes.selected[entry.leafStart]
    if (count === 0) {
        return 'none'
    }

    return count === total ? 'all' : 'some'
}

// Leaf keys in leaves[start..end) that are visible under the search.
export function leafKeysInRange(start, end, leaves, leafFlatIndex, mask) {
    const keys = []
    for (let j = start; j < end; j++) {
        if (mask === null || mask.visible[leafFlatIndex[j]] === 1) {
            keys.push(leaves[j])
        }
    }

    return keys
}

// Adds or removes keys and returns a new array. Selected keys that are not in
// `keys` (other sites, points not loaded yet, stale ids) are kept.
export function applySelection(selectedSet, keys, selected) {
    const next = new Set(selectedSet)
    for (const key of keys) {
        if (selected) {
            next.add(key)
        } else {
            next.delete(key)
        }
    }

    return Array.from(next)
}

// Which rows to render for a scroll position, plus the spacer heights around
// them. scrollTop is clamped to the list's current height first: after
// Collapse all from deep in a long list, the stored scrollTop still points
// far past the end of the now-short list, and an unclamped window would
// slice past the last row and render nothing.
export function windowRange(rowCount, scrollTop, rowHeight, viewportHeight, buffer) {
    const maxTop = Math.max(0, rowCount * rowHeight - viewportHeight)
    const top = Math.min(Math.max(0, scrollTop), maxTop)
    const start = Math.max(0, Math.floor(top / rowHeight) - buffer)
    const end = Math.min(rowCount, Math.ceil((top + viewportHeight) / rowHeight) + buffer)

    return {start, end, padTop: start * rowHeight, padBottom: (rowCount - end) * rowHeight}
}
